if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.setupGalleryControls();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setupGalleryControls() {
        this.galleryControls = this.querySelector('[data-sc-gallery-controls]');
        if (!this.galleryControls) return;
        this.galleryPrevious = this.galleryControls.querySelector('[data-sc-gallery-prev]');
        this.galleryNext = this.galleryControls.querySelector('[data-sc-gallery-next]');
        this.galleryPrevious.addEventListener('click', () => this.stepGallery(-1));
        this.galleryNext.addEventListener('click', () => this.stepGallery(1));
        this.elements.viewer.addEventListener('slideChanged', event => {
          if (event.detail.currentElement) this.syncGalleryControls(event.detail.currentElement);
        });
        this.elements.viewer.addEventListener('keydown', event => {
          if (!event.target.closest('.product__modal-opener')) return;
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.stepGallery(event.key === 'ArrowRight' ? 1 : -1);
        });
        this.galleryObserver = new MutationObserver(() => this.syncGalleryControls());
        this.galleryObserver.observe(this.elements.viewer, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
        this.classList.add('sc-product-gallery-ready');
        this.syncGalleryControls();
      }

      galleryItems() {
        return [...this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')];
      }

      syncGalleryControls(selected) {
        if (!this.galleryControls) return;
        const items = this.galleryItems();
        if (selected) this.galleryMediaId = selected.dataset.mediaId;
        else {
          const active = items.find(item => item.classList.contains('is-active'));
          if (active) this.galleryMediaId = active.dataset.mediaId;
        }
        const index = Math.max(0, items.findIndex(item => item.dataset.mediaId === this.galleryMediaId));
        this.galleryControls.hidden = items.length < 2;
        this.galleryControls.querySelector('[data-sc-gallery-current]').textContent = index + 1;
        this.galleryControls.querySelector('[data-sc-gallery-total]').textContent = items.length;
        this.galleryPrevious.disabled = index === 0;
        this.galleryNext.disabled = index === items.length - 1;
      }

      stepGallery(direction) {
        const items = this.galleryItems();
        const index = Math.max(0, items.findIndex(item => item.dataset.mediaId === this.galleryMediaId));
        const next = items[index + direction];
        if (next) this.setActiveMedia(next.dataset.mediaId, false);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.syncGalleryControls(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
